import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { deliveryAPI } from '../api/api';
import { useAuth } from '../App';
import { DeliveryStatusBadge } from '../components/DriverStatusBadge';
import RatingModal from '../components/RatingModal';
import MapPreview from '../components/MapPreview';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

export default function DeliveryDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [error, setError] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [driverCoords, setDriverCoords] = useState(null);
  const [payingOut, setPayingOut] = useState(false);
  const [payoutError, setPayoutError] = useState('');

  useEffect(() => {
    deliveryAPI.getById(id)
      .then(res => { setDelivery(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  // ── Live driver tracking ──────────────────────────────────────────────
  // While this delivery is active and has a driver, join its socket room and
  // update the driver dot as GPS pings arrive. The server scopes
  // 'driver:location' to the delivery-<id> room, so anything received here is
  // for this delivery.
  useEffect(() => {
    if (!delivery) return;
    const live = delivery.driver && ['assigned', 'accepted', 'picked_up'].includes(delivery.status);
    if (!live) return;

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => socket.emit('join:delivery', { deliveryId: id }));
    socket.on('driver:location', ({ coordinates }) => {
      if (Array.isArray(coordinates) && coordinates.length === 2) {
        setDriverCoords(coordinates);
      }
    });

    return () => { socket.disconnect(); };
  }, [delivery, id]);

  async function handlePayout() {
    setPayingOut(true);
    setPayoutError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/finance/deliveries/${id}/payout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('starfuel_token')}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Re-fetch the delivery so the UI reflects the true paid state of BOTH
        // tracks (base payout + tip payout). Patching only `payout` locally left
        // the tip showing as owed, keeping the button active after a full payout.
        const refreshed = await deliveryAPI.getById(id);
        setDelivery(refreshed.data);
      } else {
        setPayoutError(data.error || 'Payout failed');
      }
    } catch (err) {
      setPayoutError('Payout failed — please try again');
    } finally {
      setPayingOut(false);
    }
  }

  async function handleCancel() {
    if (!cancelReason.trim()) return setError('Please provide a cancellation reason');
    setCancelling(true);
    try {
      await deliveryAPI.updateStatus(id, 'cancelled', cancelReason);
      setShowCancelModal(false);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Cancellation failed');
    } finally {
      setCancelling(false);
    }
  }

  const canCancel = delivery && ['pending', 'assigned'].includes(delivery.status);
  const isLiveTracking = delivery && delivery.driver && ['assigned', 'accepted', 'picked_up'].includes(delivery.status);

  // True amount owed on this delivery, mirroring the backend payout logic:
  // base (driverGuaranteed) if unpaid + verified tip (rated, >0) if unpaid.
  const baseOwed = delivery && delivery.payout?.status !== 'paid' ? (delivery.driverGuaranteed || 0) : 0;
  const tipVerified = delivery?.ratings?.restaurantRated && (delivery?.ratings?.tip || 0) > 0;
  const tipOwed = (tipVerified && delivery?.tipPayout?.status !== 'paid') ? delivery.ratings.tip : 0;
  const totalOwed = baseOwed + tipOwed;

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" /></div>;

  if (!delivery) return (
    <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">
      <div className="text-center"><p className="text-4xl mb-3">📦</p><p>Delivery not found</p><Link to="/dashboard" className="text-yellow-400 hover:underline mt-3 block">Back to Dashboard</Link></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-gray-400 hover:text-white transition">← Back</Link>
          <h1 className="font-bold">Delivery Details</h1>
        </div>
        <DeliveryStatusBadge status={delivery.status} />
      </header>
      <main className="px-4 md:px-8 py-8 max-w-3xl mx-auto space-y-5">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <h3 className="font-semibold text-yellow-400 mb-3">📍 Pickup</h3>
          <p className="text-white">{delivery.pickup?.address}</p>
          {delivery.pickup?.notes && <p className="text-gray-400 text-sm mt-1">📝 {delivery.pickup.notes}</p>}
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <h3 className="font-semibold text-green-400 mb-3">🎯 Dropoff</h3>
          <p className="text-white">{delivery.dropoff?.address}</p>
          {delivery.dropoff?.recipientName && <p className="text-gray-400 text-sm mt-1">👤 {delivery.dropoff.recipientName}</p>}
          {delivery.dropoff?.recipientPhone && <p className="text-gray-400 text-sm">📞 {delivery.dropoff.recipientPhone}</p>}
          {delivery.dropoff?.notes && <p className="text-gray-400 text-sm">📝 {delivery.dropoff.notes}</p>}
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">🗺️ Route</h3>
            {isLiveTracking && (
              <span className="flex items-center gap-1.5 text-xs text-blue-400">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                {driverCoords ? 'Live driver location' : 'Waiting for driver…'}
              </span>
            )}
          </div>
          <MapPreview
            pickupCoords={delivery.pickup?.coordinates?.coordinates}
            dropoffCoords={delivery.dropoff?.coordinates?.coordinates}
            driverCoords={driverCoords}
            height="320px"
          />
        </div>
        {delivery.driver && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <h3 className="font-semibold text-blue-400 mb-3">🚗 Driver</h3>
            <p className="text-white font-medium">{delivery.driver.name}</p>
            <p className="text-gray-400 text-sm mt-1">{delivery.driver.phone}</p>
          </div>
        )}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <h3 className="font-semibold mb-3">Pricing</h3>
          <div className="space-y-2">
            {[
              { label: 'Delivery fee', value: `$${delivery.fee}`, color: 'text-yellow-400' },
              { label: 'Driver guaranteed', value: `$${delivery.driverGuaranteed}`, color: 'text-white' },
              { label: 'Created', value: new Date(delivery.createdAt).toLocaleString(), color: 'text-white' },
            ].map(r => (
              <div key={r.label} className="flex justify-between text-sm">
                <span className="text-gray-400">{r.label}</span>
                <span className={r.color}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
        {delivery.timeline && Object.values(delivery.timeline).some(v => v) && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <h3 className="font-semibold mb-3">Timeline</h3>
            <div className="space-y-2">
              {[
                { label: 'Assigned', time: delivery.timeline.assignedAt },
                { label: 'Accepted', time: delivery.timeline.acceptedAt },
                { label: 'Picked Up', time: delivery.timeline.pickedUpAt },
                { label: 'Delivered', time: delivery.timeline.deliveredAt },
                { label: 'Cancelled', time: delivery.timeline.cancelledAt },
              ].filter(t => t.time).map(t => (
                <div key={t.label} className="flex justify-between text-sm">
                  <span className="text-gray-400">{t.label}</span>
                  <span className="text-white">{new Date(t.time).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {delivery.status === 'cancelled' && delivery.cancellation && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <p className="text-red-400 text-sm font-medium">Cancelled — {delivery.cancellation.reason}</p>
          </div>
        )}
        {delivery.status === 'delivered' && !delivery.ratings?.restaurantRated && user.role === 'restaurant' && (
          <button onClick={() => setShowRatingModal(true)} className="w-full py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition">
            ⭐ Rate Driver & Add Tip
          </button>
        )}
        {delivery.status === 'delivered' && delivery.ratings?.restaurantRated && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
            <p className="text-green-400 text-sm font-medium">✓ Rated {delivery.ratings.driverRating}/5 stars</p>
            {delivery.ratings.tip > 0 && <p className="text-gray-400 text-xs mt-1">${delivery.ratings.tip} tip sent to driver</p>}
          </div>
        )}
        {delivery.status === 'delivered' && user.role === 'admin' && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <h3 className="font-semibold mb-3">Driver Payout</h3>
            {totalOwed <= 0 ? (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="text-green-400 text-sm font-medium">
                  ✓ Paid in full to {delivery.driver?.name}
                </p>
                {delivery.payout?.paidAt && (
                  <p className="text-gray-400 text-xs mt-1">Base ${((delivery.payout.amount || 0) / 100).toFixed(2)} • {new Date(delivery.payout.paidAt).toLocaleString()}</p>
                )}
                {delivery.tipPayout?.status === 'paid' && (
                  <p className="text-gray-400 text-xs mt-1">Tip ${((delivery.tipPayout.amount || 0) / 100).toFixed(2)} paid</p>
                )}
                {delivery.payout?.stripeTransferId && (
                  <p className="text-gray-500 text-xs mt-1">Transfer {delivery.payout.stripeTransferId}</p>
                )}
              </div>
            ) : !delivery.driver ? (
              <p className="text-gray-400 text-sm">No driver assigned to this delivery.</p>
            ) : !delivery.driver?.payoutsEnabled ? (
              <>
                <button disabled
                  className="w-full py-3 bg-gray-800 text-gray-500 font-bold rounded-xl cursor-not-allowed">
                  Pay Driver ${totalOwed.toFixed(2)}
                </button>
                <p className="text-yellow-400/80 text-xs mt-2">⚠️ {delivery.driver.name} hasn't completed payout onboarding yet.</p>
              </>
            ) : (
              <>
                <button onClick={handlePayout} disabled={payingOut}
                  className="w-full py-3 bg-green-500 hover:bg-green-400 text-white font-bold rounded-xl transition disabled:opacity-50">
                  {payingOut ? 'Paying…' : `Pay Driver $${totalOwed.toFixed(2)}`}
                </button>
                {tipOwed > 0 && (
                  <p className="text-gray-400 text-xs mt-2">Base ${baseOwed.toFixed(2)} + verified tip ${tipOwed.toFixed(2)}</p>
                )}
                {payoutError && <p className="text-red-400 text-xs mt-2">{payoutError}</p>}
              </>
            )}
          </div>
        )}
        {canCancel && (user.role === 'restaurant' || user.role === 'admin') && (
          <button onClick={() => setShowCancelModal(true)}
            className="w-full py-3 border border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-xl transition font-medium">
            Cancel Delivery
          </button>
        )}
      </main>

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-2">Cancel Delivery</h2>
            <p className="text-gray-400 text-sm mb-5">
              {delivery.status === 'assigned' ? '⚠️ A driver is assigned. This cancellation is non-refundable.' : 'No driver assigned yet. You will receive a full refund.'}
            </p>
            <div className="mb-4">
              <label className="block text-xs text-gray-400 mb-1.5">Reason for cancellation</label>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3}
                className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition resize-none"
                placeholder="Customer cancelled, wrong address, etc..." />
            </div>
            {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm mb-4">{error}</div>}
            <div className="flex gap-3">
              <button onClick={() => { setShowCancelModal(false); setError(''); }}
                className="flex-1 py-3 border border-gray-700 text-white rounded-xl hover:border-gray-500 transition">Keep Delivery</button>
              <button onClick={handleCancel} disabled={cancelling}
                className="flex-1 py-3 bg-red-500 hover:bg-red-400 text-white font-bold rounded-xl transition disabled:opacity-50">
                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showRatingModal && (
        <RatingModal
          delivery={delivery}
          userRole={user.role}
          token={localStorage.getItem('starfuel_token')}
          onClose={() => setShowRatingModal(false)}
          onSubmit={() => setDelivery(prev => ({ ...prev, ratings: { ...prev.ratings, restaurantRated: true } }))}
        />
      )}
    </div>
  );
}
